import type React from "react";
import { useCallback, useMemo } from "react";
import { Button } from "./button";
import type { QuickFilterDef } from "./types";

interface DataTableQuickFiltersProps<T> {
	quickFilters: QuickFilterDef<T>[];
	activeFilter: string;
	onChange: (filterId: string) => void;
}

function DataTableQuickFilters<T>({
	quickFilters,
	activeFilter,
	onChange,
}: DataTableQuickFiltersProps<T>) {
	// Use the helper function for click events to avoid creating a new function for each filter
	const createClickHandler = useCallback(
		(filterId: string) => {
			return (e: React.MouseEvent) => {
				e.preventDefault();
				// Only call onChange if the filter is different from the active one
				if (filterId !== activeFilter) {
					onChange(filterId);
				}
			};
		},
		[onChange, activeFilter],
	);

	// Render the filter buttons
	const filterButtons = useMemo(() => {
		// Only render buttons from the quickFilters array - no hardcoded buttons
		if (!quickFilters || quickFilters.length === 0) {
			return [];
		}

		return quickFilters.map((filter) => (
			<Button
				key={`quick-filter-${filter.id}`} // Ensure unique keys with a prefix
				variant={activeFilter === filter.id ? "default" : "outline"}
				size="sm"
				onClick={createClickHandler(filter.id)}
			>
				{filter.label}
			</Button>
		));
	}, [quickFilters, activeFilter, createClickHandler]);

	if (!quickFilters || quickFilters.length === 0) {
		return null;
	}

	return <div className="flex flex-wrap gap-1">{filterButtons}</div>;
}

// Add displayName for debugging
DataTableQuickFilters.displayName = "DataTableQuickFilters";

export { DataTableQuickFilters };
